<script setup lang="ts">
import { computed } from 'vue';
import { FolderHeart } from 'lucide-vue-next';
import CardPreview from '@/components/CardPreview.vue';
import {
    Empty,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
} from '@/components/ui/empty';
import useApp from '@/composables/useApp.ts';
import type { Element } from '@/types';

// Reads app.favorites rather than a prop, so removing a favorite anywhere on
// the page (the file sheet, the sidebar popover) drops the card right away.
// useFavorites() writes the server's new list back to app.favorites.

const labels: Record<string, string> = {
    file: 'Dokumente',
    entry: 'Seiten',
};

const app = useApp();

const favorites = computed<Element[]>(() => app.favorites ?? []);

const groups = computed(() =>
    favorites.value.reduce<Record<string, Element[]>>((groups, favorite) => {
        (groups[favorite.type] ??= []).push(favorite);
        return groups;
    }, {})
);
</script>

<template>
    <template v-if="favorites.length">
        <template
            v-for="(elements, type) in groups"
            :key="type"
        >
            <p class="p mb-4 font-bold">{{ labels[type] ?? 'Sonstiges' }}</p>
            <div class="mb-6 grid grid-cols-2 gap-6">
                <CardPreview
                    v-for="element in elements"
                    :key="element.uid"
                    :type="element.type"
                    :uid="element.uid"
                    :title="element.title"
                    :subtitle="element.subtitle"
                    :url="element.url"
                    :direct-link="element.directLink"
                    :direct-link-target="element.directLinkTarget"
                    :preview="element.preview"
                    :badge="element.badge"
                    :is-new="element.isNew"
                />
            </div>
        </template>
    </template>
    <Empty v-else>
        <EmptyHeader>
            <EmptyMedia variant="icon">
                <FolderHeart />
            </EmptyMedia>
            <EmptyTitle>Ihre Merkliste ist leer</EmptyTitle>
            <EmptyDescription
                >Fügen Sie Dokumente oder Seiten zu Ihrer Merkliste hinzu, um
                schnell Zugriff darauf zu erhalten.</EmptyDescription
            >
        </EmptyHeader>
    </Empty>
</template>
